import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Plus,
  Search,
  MoreVertical,
  Pencil,
  Trash2,
  Package2,
  X,
  Loader2,
  History,
  Cog,
  FileDown,
  Users,
  Clock,
} from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StandardItems = () => {
  const [items, setItems] = useState([]);
  const [parts, setParts] = useState([]);
  const [labourItems, setLabourItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [categories, setCategories] = useState([]);
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingItem, setDeletingItem] = useState(null);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [selectedHistory, setSelectedHistory] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category: "",
    sku: "",
    bom: [],
    labour_bom: [],
    change_notes: "",
  });
  
  // BOM editor state - Parts
  const [showAddPart, setShowAddPart] = useState(false);
  const [selectedPartId, setSelectedPartId] = useState("");
  const [partQuantity, setPartQuantity] = useState("1");
  const [partCostOverride, setPartCostOverride] = useState("");
  const [partLeadTimeDays, setPartLeadTimeDays] = useState("");
  
  // BOM editor state - Labour
  const [showAddLabour, setShowAddLabour] = useState(false);
  const [selectedLabourId, setSelectedLabourId] = useState("");
  const [labourHours, setLabourHours] = useState("1");
  const [labourRateOverride, setLabourRateOverride] = useState("");
  const [labourLeadTimeDays, setLabourLeadTimeDays] = useState("");

  const fetchData = useCallback(async () => {
    try {
      const [itemsRes, partsRes, categoriesRes, labourRes] = await Promise.all([
        axios.get(`${API}/standard-items`, { withCredentials: true }),
        axios.get(`${API}/parts`, { withCredentials: true }),
        axios.get(`${API}/standard-items/categories`, { withCredentials: true }),
        axios.get(`${API}/labour`, { withCredentials: true }),
      ]);
      setItems(itemsRes.data || []);
      setParts(partsRes.data || []);
      setCategories(categoriesRes.data || []);
      setLabourItems(labourRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load standard items");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredItems = items.filter(item => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      if (!item.name.toLowerCase().includes(query) && 
          !(item.sku || "").toLowerCase().includes(query)) {
        return false;
      }
    }
    if (categoryFilter !== "all" && item.category !== categoryFilter) {
      return false;
    }
    return true;
  });

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      category: "",
      sku: "",
      bom: [],
      labour_bom: [],
      change_notes: "",
    });
    setShowAddPart(false);
    setSelectedPartId("");
    setPartQuantity("1");
    setPartCostOverride("");
    setShowAddLabour(false);
    setSelectedLabourId("");
    setLabourHours("1");
    setLabourRateOverride("");
  };

  const openCreateDialog = () => {
    resetForm();
    setEditingItem(null);
    setDialogOpen(true);
  };

  const openEditDialog = async (item) => {
    // Fetch full item with BOM
    try {
      const res = await axios.get(`${API}/standard-items/${item.item_id}`, { withCredentials: true });
      const fullItem = res.data;
      setEditingItem(fullItem);
      setFormData({
        name: fullItem.name || "",
        description: fullItem.description || "",
        category: fullItem.category || "",
        sku: fullItem.sku || "",
        bom: fullItem.bom || [],
        labour_bom: fullItem.labour_bom || [],
        change_notes: "",
      });
      setDialogOpen(true);
    } catch (error) {
      toast.error("Failed to load item details");
    }
  };

  const handleAddPartToBOM = () => {
    if (!selectedPartId) {
      toast.error("Select a part");
      return;
    }
    
    const part = parts.find(p => p.part_id === selectedPartId);
    if (!part) return;
    
    // Check if part already in BOM
    if (formData.bom.some(b => b.part_id === selectedPartId)) {
      toast.error("Part already in BOM");
      return;
    }
    
    const quantity = parseFloat(partQuantity) || 1;
    const unitCost = partCostOverride ? parseFloat(partCostOverride) : part.unit_cost;
    
    const newBomLine = {
      part_id: part.part_id,
      part_name: part.name,
      part_sku: part.sku,
      supplier_name: part.supplier_name,
      quantity: quantity,
      unit_cost: unitCost,
      line_total: quantity * unitCost,
      lead_time_days: partLeadTimeDays ? parseInt(partLeadTimeDays) : (part.lead_time_days || null),
    };
    
    setFormData({
      ...formData,
      bom: [...formData.bom, newBomLine],
    });
    
    // Reset add part form
    setSelectedPartId("");
    setPartQuantity("1");
    setPartCostOverride("");
    setPartLeadTimeDays("");
    setShowAddPart(false);
  };

  const handleRemoveFromBOM = (partId) => {
    setFormData({
      ...formData,
      bom: formData.bom.filter(b => b.part_id !== partId),
    });
  };

  const handleUpdateBOMQuantity = (partId, newQty) => {
    const quantity = parseFloat(newQty) || 0;
    setFormData({
      ...formData,
      bom: formData.bom.map(b => {
        if (b.part_id === partId) {
          return {
            ...b,
            quantity: quantity,
            line_total: quantity * b.unit_cost,
          };
        }
        return b;
      }),
    });
  };

  // Labour BOM handlers
  const handleAddLabourToBOM = () => {
    if (!selectedLabourId) {
      toast.error("Select a labour type");
      return;
    }
    
    const labour = labourItems.find(l => l.labour_id === selectedLabourId);
    if (!labour) return;
    
    // Check if labour already in BOM
    if (formData.labour_bom.some(b => b.labour_id === selectedLabourId)) {
      toast.error("Labour type already in BOM");
      return;
    }
    
    const hours = parseFloat(labourHours) || 1;
    const hourlyRate = labourRateOverride ? parseFloat(labourRateOverride) : labour.hourly_rate;
    
    const newLabourLine = {
      labour_id: labour.labour_id,
      labour_name: labour.name,
      hours: hours,
      hourly_rate: hourlyRate,
      line_total: hours * hourlyRate,
      lead_time_days: labourLeadTimeDays ? parseInt(labourLeadTimeDays) : null,
      supplier_name: labour.supplier_name || "",
    };
    
    setFormData({
      ...formData,
      labour_bom: [...formData.labour_bom, newLabourLine],
    });
    
    // Reset add labour form
    setSelectedLabourId("");
    setLabourHours("1");
    setLabourRateOverride("");
    setLabourLeadTimeDays("");
    setShowAddLabour(false);
  };

  const handleRemoveLabourFromBOM = (labourId) => {
    setFormData({
      ...formData,
      labour_bom: formData.labour_bom.filter(b => b.labour_id !== labourId),
    });
  };

  const handleUpdateLabourHours = (labourId, newHours) => {
    const hours = parseFloat(newHours) || 0;
    setFormData({
      ...formData,
      labour_bom: formData.labour_bom.map(b => {
        if (b.labour_id === labourId) {
          return {
            ...b,
            hours: hours,
            line_total: hours * b.hourly_rate,
          };
        }
        return b;
      }),
    });
  };

  const calculatePartsCost = () => {
    return formData.bom.reduce((sum, line) => sum + (line.line_total || 0), 0);
  };

  const calculateLabourCost = () => {
    return formData.labour_bom.reduce((sum, line) => sum + (line.line_total || 0), 0);
  };

  const calculateTotalCost = () => {
    return calculatePartsCost() + calculateLabourCost();
  };

  // Group BOM items by supplier
  const groupBOMBySupplier = () => {
    const groups = {};
    
    // Add parts
    formData.bom.forEach(item => {
      const supplier = item.supplier_name || "No Supplier";
      if (!groups[supplier]) {
        groups[supplier] = { parts: [], labour: [] };
      }
      groups[supplier].parts.push(item);
    });
    
    // Add labour
    formData.labour_bom.forEach(item => {
      const supplier = item.supplier_name || "No Supplier";
      if (!groups[supplier]) {
        groups[supplier] = { parts: [], labour: [] };
      }
      groups[supplier].labour.push(item);
    });
    
    return groups;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name) {
      toast.error("Name is required");
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        name: formData.name,
        description: formData.description || null,
        category: formData.category || null,
        sku: formData.sku || null,
        bom: formData.bom.map(b => ({
          part_id: b.part_id,
          quantity: b.quantity,
          unit_cost: b.unit_cost,
          notes: b.notes || null,
        })),
        labour_bom: formData.labour_bom.map(l => ({
          labour_id: l.labour_id,
          hours: l.hours,
          hourly_rate: l.hourly_rate,
          notes: l.notes || null,
        })),
        change_notes: formData.change_notes || null,
      };
      
      if (editingItem) {
        await axios.put(`${API}/standard-items/${editingItem.item_id}`, payload, { withCredentials: true });
        toast.success("Standard item updated");
      } else {
        await axios.post(`${API}/standard-items`, payload, { withCredentials: true });
        toast.success("Standard item created");
      }
      
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingItem) return;
    
    try {
      await axios.delete(`${API}/standard-items/${deletingItem.item_id}`, { withCredentials: true });
      toast.success("Standard item deleted");
      setDeleteDialogOpen(false);
      setDeletingItem(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete");
    }
  };

  const openBOMHistory = async (item) => {
    try {
      const res = await axios.get(`${API}/standard-items/${item.item_id}/bom-history`, { withCredentials: true });
      setSelectedHistory({
        name: item.name,
        ...res.data,
      });
      setHistoryDialogOpen(true);
    } catch (error) {
      toast.error("Failed to load BOM history");
    }
  };

  if (loading) {
    return (
      <div className="p-6 md:p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-48"></div>
          <div className="h-96 bg-slate-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8" data-testid="standard-items-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900 tracking-tight">Standard Items</h1>
          <p className="text-slate-500 mt-1">Manufactured products with Bill of Materials</p>
        </div>
        <Button onClick={openCreateDialog} className="rounded-sm bg-slate-900 hover:bg-slate-800">
          <Plus className="h-4 w-4 mr-2" />
          New Standard Item
        </Button>
      </div>

      {/* Filters */}
      <Card className="rounded-sm border-slate-200 mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search items..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 rounded-sm"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-48 rounded-sm">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Items Table */}
      <Card className="rounded-sm border-slate-200">
        <CardContent className="p-0">
          {filteredItems.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Item</TableHead>
                  <TableHead>SKU</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead className="text-center">Parts</TableHead>
                  <TableHead className="text-right">Total Cost</TableHead>
                  <TableHead className="text-center">Version</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredItems.map((item) => (
                  <TableRow 
                    key={item.item_id}
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => openEditDialog(item)}
                    data-testid={`standard-item-row-${item.item_id}`}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-indigo-100 rounded-sm flex items-center justify-center">
                          <Package2 className="h-5 w-5 text-indigo-600" />
                        </div>
                        <div>
                          <p className="font-medium">{item.name}</p>
                          {item.description && (
                            <p className="text-xs text-slate-400 truncate max-w-[200px]">{item.description}</p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {item.sku ? (
                        <code className="text-sm bg-slate-100 px-2 py-0.5 rounded">{item.sku}</code>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {item.category ? (
                        <Badge variant="outline" className="rounded-sm">{item.category}</Badge>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex gap-2 justify-center">
                        <Badge className="rounded-sm bg-blue-50 text-blue-700 border border-blue-200">
                          <Cog className="h-3 w-3 mr-1" />{item.bom_count || 0}
                        </Badge>
                        {(item.labour_count || 0) > 0 && (
                          <Badge className="rounded-sm bg-purple-50 text-purple-700 border border-purple-200">
                            <Users className="h-3 w-3 mr-1" />{item.labour_count}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      R{(item.total_cost || 0).toFixed(2)}
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="text-sm text-slate-500">v{item.bom_version || 1}</span>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEditDialog(item)}>
                            <Pencil className="mr-2 h-4 w-4" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openBOMHistory(item)}>
                            <History className="mr-2 h-4 w-4" />
                            BOM History
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={() => { setDeletingItem(item); setDeleteDialogOpen(true); }}
                            className="text-red-600"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-16">
              <Package2 className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 text-lg font-medium">No standard items</p>
              <p className="text-slate-400 text-sm mt-1">
                {searchQuery || categoryFilter !== "all"
                  ? "Try adjusting your filters"
                  : "Create your first standard item with a bill of materials"}
              </p>
              {!searchQuery && categoryFilter === "all" && (
                <Button onClick={openCreateDialog} variant="outline" className="mt-4 rounded-sm">
                  <Plus className="h-4 w-4 mr-2" />
                  New Standard Item
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog with BOM Editor */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingItem ? "Edit Standard Item" : "New Standard Item"}</DialogTitle>
            <DialogDescription>
              {editingItem ? "Update item details and bill of materials" : "Create a manufactured item with its required parts"}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 md:col-span-1">
                <Label>Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="4-Tap Draught Beer System"
                  className="rounded-sm mt-1"
                />
              </div>
              <div className="col-span-2 md:col-span-1">
                <Label>SKU</Label>
                <Input
                  value={formData.sku}
                  onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                  placeholder="DBS-4TAP-001"
                  className="rounded-sm mt-1"
                />
              </div>
              <div className="col-span-2 md:col-span-1">
                <Label>Category</Label>
                <Input
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  placeholder="Beer Systems"
                  className="rounded-sm mt-1"
                />
              </div>
              <div className="col-span-2">
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Complete 4-tap draught system for small bars"
                  className="rounded-sm mt-1"
                  rows={2}
                />
              </div>
            </div>

            {/* BOM Section with Tabs for Parts and Labour */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-lg">Bill of Materials</h3>
                <div className="flex gap-2">
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={() => { setShowAddPart(!showAddPart); setShowAddLabour(false); }}
                    className="rounded-sm"
                  >
                    <Cog className="h-4 w-4 mr-1" />
                    Add Part
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={() => { setShowAddLabour(!showAddLabour); setShowAddPart(false); }}
                    className="rounded-sm"
                  >
                    <Users className="h-4 w-4 mr-1" />
                    Add Labour
                  </Button>
                </div>
              </div>

              {/* Add Part Form */}
              {showAddPart && (
                <Card className="rounded-sm border-dashed border-blue-200 bg-blue-50/30 mb-4">
                  <CardContent className="p-4">
                    <p className="text-xs font-medium text-blue-700 mb-3">Add Part Component</p>
                    <div className="grid grid-cols-12 gap-3 items-end">
                      <div className="col-span-4">
                        <Label className="text-xs">Part</Label>
                        <Select value={selectedPartId} onValueChange={setSelectedPartId}>
                          <SelectTrigger className="rounded-sm mt-1">
                            <SelectValue placeholder="Select part" />
                          </SelectTrigger>
                          <SelectContent>
                            {parts.filter(p => !formData.bom.some(b => b.part_id === p.part_id)).map(part => (
                              <SelectItem key={part.part_id} value={part.part_id}>
                                {part.name} ({part.sku}) {part.supplier_name ? `- ${part.supplier_name}` : ""}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs">Quantity</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={partQuantity}
                          onChange={(e) => setPartQuantity(e.target.value)}
                          className="rounded-sm mt-1"
                        />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs">Cost Override</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={partCostOverride}
                          onChange={(e) => setPartCostOverride(e.target.value)}
                          placeholder={selectedPartId ? parts.find(p => p.part_id === selectedPartId)?.unit_cost?.toString() : ""}
                          className="rounded-sm mt-1"
                        />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs">Lead Time (days)</Label>
                        <Input
                          type="number"
                          min="0"
                          value={partLeadTimeDays}
                          onChange={(e) => setPartLeadTimeDays(e.target.value)}
                          placeholder="0"
                          className="rounded-sm mt-1"
                        />
                      </div>
                      <div className="col-span-2">
                        <Button 
                          type="button" 
                          onClick={handleAddPartToBOM}
                          className="rounded-sm w-full"
                          size="sm"
                        >
                          Add
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Add Labour Form */}
              {showAddLabour && (
                <Card className="rounded-sm border-dashed border-purple-200 bg-purple-50/30 mb-4">
                  <CardContent className="p-4">
                    <p className="text-xs font-medium text-purple-700 mb-3">Add Labour Component</p>
                    <div className="grid grid-cols-12 gap-3 items-end">
                      <div className="col-span-4">
                        <Label className="text-xs">Labour Type</Label>
                        <Select value={selectedLabourId} onValueChange={setSelectedLabourId}>
                          <SelectTrigger className="rounded-sm mt-1">
                            <SelectValue placeholder="Select labour" />
                          </SelectTrigger>
                          <SelectContent>
                            {labourItems.filter(l => !formData.labour_bom.some(b => b.labour_id === l.labour_id)).map(labour => (
                              <SelectItem key={labour.labour_id} value={labour.labour_id}>
                                {labour.name} (R{labour.hourly_rate}/hr) {labour.supplier_name ? `- ${labour.supplier_name}` : ""}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs">Hours</Label>
                        <Input
                          type="number"
                          step="0.5"
                          value={labourHours}
                          onChange={(e) => setLabourHours(e.target.value)}
                          className="rounded-sm mt-1"
                        />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs">Rate Override</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={labourRateOverride}
                          onChange={(e) => setLabourRateOverride(e.target.value)}
                          placeholder={selectedLabourId ? labourItems.find(l => l.labour_id === selectedLabourId)?.hourly_rate?.toString() : ""}
                          className="rounded-sm mt-1"
                        />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs">Lead Time (days)</Label>
                        <Input
                          type="number"
                          min="0"
                          value={labourLeadTimeDays}
                          onChange={(e) => setLabourLeadTimeDays(e.target.value)}
                          placeholder="0"
                          className="rounded-sm mt-1"
                        />
                      </div>
                      <div className="col-span-2">
                        <Button 
                          type="button" 
                          onClick={handleAddLabourToBOM}
                          className="rounded-sm w-full"
                          size="sm"
                        >
                          Add
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* BOM Display - Grouped by Supplier */}
              {(formData.bom.length > 0 || formData.labour_bom.length > 0) ? (
                <div className="space-y-4">
                  {Object.entries(groupBOMBySupplier()).map(([supplier, items]) => (
                    <div key={supplier} className="border rounded-sm overflow-hidden">
                      <div className="bg-slate-100 px-4 py-2 border-b">
                        <span className="font-medium text-sm">{supplier}</span>
                      </div>
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50">
                            <TableHead className="w-10">Type</TableHead>
                            <TableHead>Component</TableHead>
                            <TableHead className="w-20">Qty/Hrs</TableHead>
                            <TableHead className="text-right w-20">Rate</TableHead>
                            <TableHead className="text-center w-20">Lead Time</TableHead>
                            <TableHead className="text-right w-20">Total</TableHead>
                            <TableHead className="w-10"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {/* Parts */}
                          {items.parts.map((line) => (
                            <TableRow key={`part-${line.part_id}`}>
                              <TableCell>
                                <Badge variant="outline" className="rounded-sm text-xs bg-blue-50 text-blue-700 border-blue-200">
                                  <Cog className="h-3 w-3 mr-1" />Part
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <div>
                                  <p className="font-medium text-sm">{line.part_name}</p>
                                  <p className="text-xs text-slate-400">{line.part_sku}</p>
                                </div>
                              </TableCell>
                              <TableCell>
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={line.quantity}
                                  onChange={(e) => handleUpdateBOMQuantity(line.part_id, e.target.value)}
                                  className="rounded-sm h-8 w-20"
                                />
                              </TableCell>
                              <TableCell className="text-right text-sm">
                                R{line.unit_cost?.toFixed(2)}
                              </TableCell>
                              <TableCell className="text-center text-sm text-slate-500">
                                {line.lead_time_days ? `${line.lead_time_days}d` : "—"}
                              </TableCell>
                              <TableCell className="text-right font-medium">
                                R{line.line_total?.toFixed(2)}
                              </TableCell>
                              <TableCell>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleRemoveFromBOM(line.part_id)}
                                  className="h-8 w-8 text-slate-400 hover:text-red-600"
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                          {/* Labour */}
                          {items.labour.map((line) => (
                            <TableRow key={`labour-${line.labour_id}`}>
                              <TableCell>
                                <Badge variant="outline" className="rounded-sm text-xs bg-purple-50 text-purple-700 border-purple-200">
                                  <Clock className="h-3 w-3 mr-1" />Labour
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <div>
                                  <p className="font-medium text-sm">{line.labour_name}</p>
                                  <p className="text-xs text-slate-400">Hourly labour</p>
                                </div>
                              </TableCell>
                              <TableCell>
                                <Input
                                  type="number"
                                  step="0.5"
                                  value={line.hours}
                                  onChange={(e) => handleUpdateLabourHours(line.labour_id, e.target.value)}
                                  className="rounded-sm h-8 w-20"
                                />
                              </TableCell>
                              <TableCell className="text-right text-sm">
                                R{line.hourly_rate?.toFixed(2)}/hr
                              </TableCell>
                              <TableCell className="text-center text-sm text-slate-500">
                                {line.lead_time_days ? `${line.lead_time_days}d` : "—"}
                              </TableCell>
                              <TableCell className="text-right font-medium">
                                R{line.line_total?.toFixed(2)}
                              </TableCell>
                              <TableCell>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleRemoveLabourFromBOM(line.labour_id)}
                                  className="h-8 w-8 text-slate-400 hover:text-red-600"
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ))}
                  {/* Cost Summary */}
                  <div className="bg-slate-50 rounded-sm px-4 py-3 flex justify-between items-center border">
                    <div className="flex gap-6 text-sm">
                      <span className="text-slate-500">
                        <Cog className="h-4 w-4 inline mr-1" />{formData.bom.length} parts
                      </span>
                      <span className="text-slate-500">
                        <Users className="h-4 w-4 inline mr-1" />{formData.labour_bom.length} labour
                      </span>
                    </div>
                    <div className="text-right space-y-1">
                      <div className="text-xs text-slate-500">
                        Parts: R{calculatePartsCost().toFixed(2)} | Labour: R{calculateLabourCost().toFixed(2)}
                      </div>
                      <div>
                        <span className="text-sm text-slate-500 mr-2">Total:</span>
                        <span className="text-lg font-semibold">R{calculateTotalCost().toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="border border-dashed rounded-sm p-8 text-center">
                  <Package2 className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                  <p className="text-slate-500">No components added yet</p>
                  <p className="text-sm text-slate-400">Click "Add Part" or "Add Labour" to build your bill of materials</p>
                </div>
              )}
            </div>

            {/* Change Notes (for editing) */}
            {editingItem && (
              <div className="border-t pt-4">
                <Label>Change Notes (optional)</Label>
                <Input
                  value={formData.change_notes}
                  onChange={(e) => setFormData({ ...formData, change_notes: e.target.value })}
                  placeholder="What changed in this version?"
                  className="rounded-sm mt-1"
                />
                <p className="text-xs text-slate-400 mt-1">
                  Current version: v{editingItem.bom_version || 1}. Saving will create v{(editingItem.bom_version || 1) + 1}.
                </p>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">
                Cancel
              </Button>
              <Button type="submit" disabled={saving} className="rounded-sm">
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingItem ? "Save Changes" : "Create Item"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Standard Item</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deletingItem?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} className="rounded-sm">
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} className="rounded-sm">
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* BOM History Dialog */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>BOM History: {selectedHistory?.name}</DialogTitle>
            <DialogDescription>
              Current version: v{selectedHistory?.current_version}
            </DialogDescription>
          </DialogHeader>
          
          {selectedHistory?.history?.length > 0 ? (
            <div className="space-y-4">
              {selectedHistory.history.slice().reverse().map((entry) => (
                <Card key={entry.version} className="rounded-sm">
                  <CardHeader className="py-3 px-4">
                    <div className="flex items-center justify-between">
                      <Badge variant="outline" className="rounded-sm">v{entry.version}</Badge>
                      <span className="text-xs text-slate-400">
                        {new Date(entry.changed_at).toLocaleDateString()} {new Date(entry.changed_at).toLocaleTimeString()}
                      </span>
                    </div>
                    {entry.change_notes && (
                      <p className="text-sm text-slate-600 mt-1">{entry.change_notes}</p>
                    )}
                  </CardHeader>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="text-xs">Part</TableHead>
                          <TableHead className="text-xs text-center">Qty</TableHead>
                          <TableHead className="text-xs text-right">Cost</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {entry.bom.map((line) => (
                          <TableRow key={`${entry.version}-${line.part_id}`}>
                            <TableCell className="text-sm">{line.part_name}</TableCell>
                            <TableCell className="text-sm text-center">{line.quantity}</TableCell>
                            <TableCell className="text-sm text-right">R{line.line_total?.toFixed(2)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <History className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No previous versions</p>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryDialogOpen(false)} className="rounded-sm">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StandardItems;
